import React, {
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from "react";
import Button from "../UI/Button";
import { useNavigate } from "react-router-dom";
import { Flex } from "reflexbox/styled-components";
import { Body } from "../UI/Text";
import Link from "../UI/Link";
import { DropdownWrapper, HeaderWrapper, DropdownItems } from "./styles";
import { useTheme } from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { updateUser } from "../../requests/userManagementRequests";
import * as fa from "@fortawesome/free-solid-svg-icons";
import UserContext from "../../context/user/UserContext";
import * as constants from "../../modules/constants";
import SocketContext from "../../context/socket/SocketContext";
import { useQuery } from "../Hooks";
import { getGroups } from "../../requests/groupManagementRequests";
import _ from "lodash";
import Loading from "../UI/Loading";

const Header = ({ refetchTheme }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    activeUser,
    darkMode,
    setDarkMode,
    username,
    setActiveUser,
    loggedIn,
    setLoggedIn,
    platform,
    pid,
    refetchHeader,
    setRefetchHeader,
    // isLoading: userLoading,
  } = useContext(UserContext);
  const { client } = useContext(SocketContext);

  const {
    data: groupData,
    refetchQuery,
    isLoading,
  } = useQuery({
    queryID: "headergroups",
    queryVariables: { pid, username },
    forcedFilters: {},
    skip: !loggedIn,
    request: getGroups,
  });

  useEffect(() => {
    if (refetchHeader && !isLoading) {
      refetchQuery();
      setRefetchHeader(false);
    }
  }, [isLoading, refetchHeader, refetchQuery, setRefetchHeader]);

  const { rows: groups = [] } = groupData || {};

  const { groupId, groupIds } = activeUser || {};

  const groupOptions = useMemo(() => {
    if (!groupData?.rows) return null;
    const newGroups = [...groupData?.rows];
    _.remove(newGroups, ({ id: gid }) => gid === groupId);
    return newGroups;
  }, [groupData, groupId]);

  const { name } = platform || {};

  const [toggleOpen, setToggleOpen] = useState(false);
  const [leftToggleOpen, setLeftToggleOpen] = useState(false);

  const darkModeToggle = useCallback(() => {
    window.localStorage.setItem("darkMode", !darkMode);
    window.sessionStorage.setItem("darkMode", !darkMode);
    setDarkMode(!darkMode);
    refetchTheme();
  }, [darkMode, refetchTheme, setDarkMode]);

  const signOut = useCallback(async () => {
    const { username, id } = activeUser;
    if (client)
      client.send(
        JSON.stringify({
          type: "userevent",
          username,
          content: { isLoggedIn: false },
        })
      );

    await updateUser({ id, input: { isLoggedIn: false } });
    window.sessionStorage.removeItem("token");
    window.localStorage.removeItem("token");
    setActiveUser({});
    setToggleOpen(false);
    setLeftToggleOpen(false);
    setLoggedIn(false);
    navigate("/Login");
  }, [activeUser, client, setActiveUser, setLoggedIn, navigate]);

  const currentGroup = useMemo(() => {
    const currentGroup = _.find(groups, ({ id: gid }) => gid === groupId);
    return currentGroup;
  }, [groupId, groups]);

  const assignUserGroup = useCallback(
    async (group) => {
      const { id: gid } = group;
      const { id } = activeUser || {};
      setActiveUser({ ...activeUser, groupId: gid });
      await updateUser({ id, input: { groupId: gid } });
      navigate("/QuoteDashboard");
    },
    [activeUser, setActiveUser, navigate]
  );

  return (
    <HeaderWrapper className="no-print" darkMode={darkMode}>
      <Flex alignItems="center">
        <Link
          title="Go to your homepage."
          to={
            !activeUser
              ? "/"
              : constants.DEFAULT_HOMEPAGE[activeUser?.roleLevel || 0]
          }
        >
          <img src={theme.logo} alt={name || "SwiftSell"} />
        </Link>
        {isLoading ? (
          <DropdownWrapper darkMode={darkMode}>
            <Loading inline />
          </DropdownWrapper>
        ) : (
          <>
            {groupIds?.length > 0 && groups?.length > 0 && (
              <DropdownWrapper
                onMouseLeave={() => setLeftToggleOpen(false)}
                toggleOpen={leftToggleOpen}
                darkMode={darkMode}
              >
                {!currentGroup ? (
                  <Button
                    className="header-dropdown header-link"
                    iconPosition="left"
                    iconName="faUser"
                    textOnly
                    title="Select a group to view its content."
                    onClick={() => setLeftToggleOpen(!leftToggleOpen)}
                  >
                    <Body ellipsis className="username">
                      My SwiftSell
                    </Body>
                    {groupOptions?.length > 0 && (
                      <FontAwesomeIcon
                        icon={leftToggleOpen ? fa.faAngleUp : fa.faAngleDown}
                      />
                    )}
                  </Button>
                ) : (
                  <>
                    <Button
                      className="header-dropdown header-link"
                      iconPosition="left"
                      iconName="faUserGroup"
                      textOnly
                      title={`Filtering pages by group: ${currentGroup?.name}`}
                      onClick={() => setLeftToggleOpen(!leftToggleOpen)}
                    >
                      <Body ellipsis className="username">
                        {currentGroup?.name}
                      </Body>
                      {groups?.length > 0 && (
                        <FontAwesomeIcon
                          icon={leftToggleOpen ? fa.faAngleUp : fa.faAngleDown}
                        />
                      )}
                    </Button>
                  </>
                )}
                {groups.length > 0 && (
                  <DropdownItems className="open group">
                    {groupOptions.map((group) => {
                      return (
                        <Button
                          key={group?.id}
                          className="header-link"
                          textOnly
                          title={`Filter pages by group: ${group?.name}`}
                          onClick={() => assignUserGroup(group)}
                          iconName="faUserGroup"
                          iconPosition="left"
                        >
                          <Body ellipsis className="username">
                            {group?.name}
                          </Body>
                        </Button>
                      );
                    })}
                    {currentGroup && (
                      <Button
                        key="clear"
                        className="header-link"
                        textOnly
                        onClick={() => assignUserGroup({ id: null })}
                        iconName="faUser"
                        title="See only my content."
                        iconPosition="left"
                      >
                        <Body ellipsis className="username">
                          My SwiftSell
                        </Body>
                      </Button>
                    )}
                  </DropdownItems>
                )}
              </DropdownWrapper>
            )}
          </>
        )}
      </Flex>

      <Flex alignItems="center">
        {!activeUser || activeUser?.roleLevel === 0 ? (
          <>
            <Button
              className="header-link hideable-header"
              textOnly
              to="/Catalog"
              iconName="faBook"
              iconPosition="left"
              title="Go to the Product Catalog"
            >
              Product Catalog
            </Button>
          </>
        ) : (
          <>
            <Button
              className="header-link hideable-header"
              iconPosition="left"
              iconName="faFileContract"
              textOnly
              to="/QuoteDashboard"
              title="Go to the Quote Dashboard"
            >
              Quotes
            </Button>
            {/* <Button
              className="header-link hideable-header"
              iconPosition="left"
              iconName="faTruckFast"
              textOnly
              to={`/MyOrders/${activeUser?.username}`}
              title="View past Orders."
            >
              Orders
            </Button> */}
            {/* <Button
              className="header-link hideable-header"
              iconPosition="left"
              iconName="faTags"
              textOnly
              title="View and Edit RMAs."
              to={`/RMAs/${pid}`}
            >
              RMAs
            </Button> */}
          </>
        )}

        <DropdownWrapper
          onMouseLeave={() => setToggleOpen(false)}
          toggleOpen={toggleOpen}
          darkMode={darkMode}
        >
          {!loggedIn ? (
            <Button
              className="header-dropdown header-link"
              iconPosition="left"
              iconName="faUser"
              textOnly
              title="Visit the Login Page."
              to="/Login"
            >
              Login
            </Button>
          ) : (
            <>
              <Button
                className="header-dropdown header-link"
                iconPosition="left"
                iconName="faUser"
                textOnly
                onClick={() => setToggleOpen(!toggleOpen)}
              >
                <Body ellipsis className="username">
                  {username || activeUser?.username}
                </Body>
                <FontAwesomeIcon
                  icon={toggleOpen ? fa.faAngleUp : fa.faAngleDown}
                />
              </Button>
            </>
          )}

          <DropdownItems className="open">
            {!activeUser || activeUser?.roleLevel === 0 ? (
              <Button
                className="header-link hideable-dropdown"
                iconPosition="left"
                iconName="faBorderAll"
                textOnly
                to="/QuoteDashboard"
                title="Go to the Quote Dashboard"
              >
                Quotes
              </Button>
            ) : (
              <>
                <Button
                  className="header-link hideable-dropdown"
                  title="Go to the Quote Dashboard"
                  iconPosition="left"
                  iconName="faBorderAll"
                  textOnly
                  to="/QuoteDashboard"
                >
                  Quotes
                </Button>
                <Button
                  className="header-link hideable-dropdown"
                  iconPosition="left"
                  iconName="faTags"
                  textOnly
                  title="View past Orders."
                  to={`/MyOrders/${activeUser?.username}`}
                >
                  Orders
                </Button>
              </>
            )}
            {activeUser?.roleLevel >= 5 && (
              <Button
                className="header-link"
                textOnly
                to="/Administration"
                iconName="faUserGear"
                iconPosition="left"
                title="System Administration Panel"
              >
                Administration
              </Button>
            )}

            {activeUser?.roleLevel > 0 && (
              <Button
                className="header-link"
                textOnly
                to="/Catalog"
                iconName="faBook"
                iconPosition="left"
                title="Go to the Product Catalog"
              >
                Product Catalog
              </Button>
            )}

            <Button
              className="header-link"
              textOnly
              iconPosition="left"
              iconName="faUserPen"
              title="Edit your profile settings."
              to={`/MyProfile/${activeUser?.username}`}
            >
              My Profile
            </Button>

            {/* <Button
              className="header-link"
              textOnly
              to="/MyConfigurations"
              iconPosition="left"
              iconName="faPenRuler"
            >
              My Configurations
            </Button> */}
            <Button
              iconPosition="left"
              className="header-link"
              textOnly
              iconName="faArrowRightFromBracket"
              onClick={signOut}
              title="Sign out of your account"
            >
              Sign Out
            </Button>
            <Button
              title={
                darkMode
                  ? "Toggle site theme to Light Mode"
                  : "Toggle site theme to Dark Mode"
              }
              className="header-link"
              textOnly
              onClick={darkModeToggle}
            >
              <FontAwesomeIcon icon={darkMode ? fa.faMoon : fa.faSun} />
              <Body>Dark Mode</Body>
              <FontAwesomeIcon
                icon={darkMode ? fa.faToggleOn : fa.faToggleOff}
                size="lg"
              />
            </Button>
          </DropdownItems>
        </DropdownWrapper>
      </Flex>
    </HeaderWrapper>
  );
};

export default Header;
